import MaterialIcon from '@material/react-material-icon';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import { Button } from '@upmc/ims.component-library';
import { UserContext } from 'context/User/UserContext';
import { nanoid } from 'nanoid';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PersonSearch from '../../../components/UI/Input/PersonSearch/PersonSearch';
import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import { theme } from '../../../components/layouts/theme';
import {
	indexRows,
	init,
	setRows,
	sortByHeader,
	toggleRow,
} from '../../../store/adminSearch/actions';
import Base from '../../Base/Base';
import { ADMIN_HEADERS } from '../GlobalProtectExemption/_constants';
import Styles from './SSHException.module.scss';
import SSHExceptionForm from './SSHExceptionForm';
import { fetchPersonSearch } from './_api';

// Enum to represent the different steps in the SSH Exception process
const STEPS = {
	CHOOSE_REQUESTEE: 0,
	PERSON_SEARCH: 1,
	FORM: 2,
	COMPLETE: 3,
};

// Manager roles which have access to submit exception for another user
const MANAGER_ROLE_IDS = new Set([13, 15, 18, 281, 830]);

/**
 * Component for choosing whether to submit exception for self or another user.
 *
 * @returns RRequestee selection component.
 */
const ChooseRequesteeStep = ({ onSelectMyself, onSelectOther }) => (
	<>
		<Grid item>
			<Typography variant='h5'>
				Do you want to submit an exception for yourself or for another
				user?
			</Typography>
		</Grid>
		<Grid item>
			<RadioGroup row>
				<FormControlLabel
					value='self'
					control={<Radio />}
					label='Myself'
					onChange={onSelectMyself}
				/>
				<FormControlLabel
					value='other'
					control={<Radio />}
					label='Other'
					onChange={onSelectOther}
				/>
			</RadioGroup>
		</Grid>
	</>
);

/**
 * Component for searching and selecting a person.
 *
 * @returns Person search component.
 */
const PersonSearchStep = ({
	errorMessage,
	isLoading,
	tableActions,
	tableHeaders,
	tableRows,
	onSearchChange,
	onSearch,
}) => (
	<PersonSearch
		errorMessage={errorMessage}
		label='Person Search'
		dataTable={{
			label: 'Search Results',
			actions: tableActions,
			headers: tableHeaders,
			rows: tableRows.filter(row => row.isDisplayed === true),
			onTextFieldChangedHandler: e => onSearchChange(e.target.value),
		}}
		isLoading={isLoading}
		onButtonClickedHandler={onSearch}
		requestTarget='REQUESTEE'
	/>
);

/**
 * Component for the form step.
 *
 * @returns Form for submitting SSH exception request.
 */
const FormStep = ({
	user,
	userSelected,
	onFormSubmit,
	onSetTrackingNumber,
}) => (
	<SSHExceptionForm
		user={user}
		userSelected={userSelected}
		onFormSubmit={onFormSubmit}
		setTrackingNumber={onSetTrackingNumber}
	/>
);

/**
 * Component for completion step.
 *
 * @returns Completion message and tracking number if available.
 */
const CompleteStep = ({ trackingNumber, onStartAnotherRequest }) => (
	<>
		{trackingNumber ? (
			<>
				<Typography variant='subtitle2' align='center'>
					Thank you for submitting your Outbound SSH Exception
					Request!
					<br />
					<i>Please allow up to 3 business days for review.</i>
				</Typography>
				<Typography variant='subtitle2'>
					<b>Tracking Number: </b>
					{trackingNumber}
				</Typography>
			</>
		) : (
			<>
				<Typography
					variant='subtitle2'
					className={Styles['subtitle2--invalid']}
				>
					An error occurred while submitting your request.
				</Typography>
				<Typography variant='subtitle2'>
					Please try again. If issues persist, please contact IMS
					support.
				</Typography>
			</>
		)}
		<Button
			variant='outlined'
			startIcon={<MaterialIcon icon='refresh' />}
			onClick={onStartAnotherRequest}
			sx={{ margin: '5px' }}
			id='CreateAnotherRequest'
		>
			Start Another Request
		</Button>
	</>
);

/**
 * Helper function to determine the initial step based on user role
 *
 * @returns Initial step for the SSH Exception process.
 */
const getInitialStep = isSupervisor =>
	isSupervisor ? STEPS.CHOOSE_REQUESTEE : STEPS.FORM;

/**
 * Main component for Outbound SSH exceptions.
 *
 * @returns Request page.
 */
const SSHException = () => {
	// user info
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const isSupervisor =
		user?.Roles?.some(role => MANAGER_ROLE_IDS.has(role.ID)) ?? false;

	const [userSelected, setUserSelected] = useState(null);
	const [step, setStep] = useState(getInitialStep(isSupervisor));
	const [trackingNumber, setTrackingNumber] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);

	const dispatch = useDispatch();
	const rActions = useSelector(state => state.adminSearchTable.actions);
	const rHeaders = useSelector(state => state.adminSearchTable.headers);
	const rRows = useSelector(state => state.adminSearchTable.rows);

	// Increment to next step after form body is generated
	useEffect(() => {
		if (userSelected) {
			setStep(STEPS.FORM);
		}
	}, [userSelected]);

	// Initial redux store initialization for person search component
	useEffect(() => {
		dispatch(
			init({
				actions: [
					{
						Type: ACTION_TYPES.TOGGLE,
						onChangedHandler: (_, __, row) => {
							setUserSelected({
								Name: row?.Cells?.Name?.Value,
								NTID: row?.Cells?.Network_ID?.Value,
								ESignOn:
									row?.Cells?.['UPMC_E-Sign-On_ID']?.Value,
								Requestee_ESOFIID: Number(
									row?.Cells?.FI_ID?.Value
								),
								Requestor_ESOFIID: user?.ID,
								Email: row?.Cells?.Email?.Value,
								Department: row?.Cells?.Department?.Value,
								Manager: row?.Cells?.Manager?.Value,
							});
							dispatch(toggleRow(row));
							dispatch(setRows([]));
						},
					},
				],
				headers: ADMIN_HEADERS.map((header, index) => {
					return {
						Value: header,
						props: {
							onSortChange: sortOrder =>
								dispatch(sortByHeader(header, sortOrder)),
							sort: index === 0 ? 1 : null,
						},
					};
				}),
			})
		);
	}, [dispatch, user?.ID]);

	// Render the appropriate body based on the current step
	const renderBody = () => {
		switch (step) {
			case STEPS.CHOOSE_REQUESTEE:
				return (
					<ChooseRequesteeStep
						onSelectMyself={() => {
							setUserSelected(null); // Reset userSelected to empty when choosing self
							setStep(STEPS.FORM);
						}}
						onSelectOther={() => {
							setStep(STEPS.PERSON_SEARCH);
						}}
					/>
				);
			case STEPS.PERSON_SEARCH:
				return (
					<PersonSearchStep
						errorMessage={errorMessage}
						isLoading={loading}
						tableActions={rActions}
						tableHeaders={rHeaders}
						tableRows={rRows}
						onSearchChange={value => dispatch(indexRows(value))}
						onSearch={payload => {
							// Start loading
							setLoading(true);
							setErrorMessage(null);

							// Call the API to fetch person search results
							fetchPersonSearch(payload).then(response => {
								if (
									response.status === 200 &&
									response.data.responseCode === 200
								) {
									let payload = response.data.payload;

									payload.forEach(row => {
										row.ActionCells = [
											{
												BindTo: 0,
												Checked: false,
												Key: nanoid(),
												Text: 'Select User',
											},
										];
										row.isDisplayed = true;

										return row;
									});

									dispatch(setRows(payload));
								} else {
									setErrorMessage(
										response.data.messages[0].Value
									);
								}

								// Stop loading
								setLoading(false);
							});
						}}
					/>
				);
			case STEPS.FORM:
				return (
					<FormStep
						user={user}
						userSelected={userSelected}
						onFormSubmit={() => setStep(STEPS.COMPLETE)}
						onSetTrackingNumber={setTrackingNumber}
					/>
				);
			case STEPS.COMPLETE:
				return (
					<CompleteStep
						trackingNumber={trackingNumber}
						onStartAnotherRequest={() => {
							setStep(getInitialStep(isSupervisor));
						}}
					/>
				);
			default:
				// This should never be reached
				console.error('Unexpected step value:', step);

				return null;
		}
	};

	// Build and return React component
	return (
		<ThemeProvider theme={theme}>
			<Base title='Outbound SSH Exception'>
				<Grid
					container
					direction='column'
					spacing={1}
					sx={{
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					{renderBody()}
				</Grid>
			</Base>
		</ThemeProvider>
	);
};

export default SSHException;
